import { FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import classnames from "classnames";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { calculatePrice, formatPrice } from "services/pricingModel/utils";
import { trackPricingItemSelection } from "services/subscriptions/analytics";
import { useStyles } from "../../../pages/Settings/components/PlanDetail/const";
import { PricingModel, PricingModelItem } from "../../../services/pricingModel/model";
import styles from "./styles.module.scss";

interface PlanSelectorProps {
  pricingModel: PricingModel;
  selectedItem: PricingModelItem;
  setSelectedPlan: (item: PricingModelItem) => void;
  disabled?: boolean;
}

export default function PlanSelector({
  pricingModel,
  selectedItem: selectedPlan,
  setSelectedPlan,
  disabled,
}: PlanSelectorProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const onChangePlan = useCallback(
    (newPlan: PricingModelItem) => {
      if (!disabled) {
        setSelectedPlan(newPlan);
        trackPricingItemSelection(newPlan);
      }
    },
    [disabled, setSelectedPlan]
  );

  const hdChangePlan = useCallback(
    (e: any) => {
      if (e.target?.value && !disabled) {
        const plan = pricingModel.items.find(item => item.id === e.target.value);
        if (plan) {
          onChangePlan(plan);
        }
      }
    },
    [disabled, onChangePlan, pricingModel.items]
  );

  const selfStudyRadioGroup = pricingModel.items
    .filter(item => item.purchase_student_type === "self-study")
    .map(item => (
      <div
        className={classnames(styles.priceItem, {
          [styles.active]: selectedPlan.id === item.id,
        })}
        onClick={() => onChangePlan(item)}
        key={item.id}
      >
        <FormControlLabel
          disabled={disabled}
          value={item.id}
          control={
            <Radio
              className={classes.root}
              disabled={disabled}
              disableRipple
              color="default"
              checkedIcon={
                <span className={classnames(classes.icon, classes.checkedIcon)} />
              }
              icon={<span className={classes.icon} />}
            />
          }
          label={item.name}
        />
        <div className={styles.priceTag}>
          <b>{formatPrice(item.price, item.currency)}</b>
          <span className={styles.perUnit}>
            {`${formatPrice(calculatePrice(item), item.currency)}${t("Common.comparePlans.perMonth")}`}
          </span>
        </div>
      </div>
    ));

  const oneOnOneRadioGroup = pricingModel.items
    .filter(item => item.purchase_student_type === "one-on-one")
    .map(item => (
      <div
        className={classnames(styles.priceItem, {
          [styles.active]: selectedPlan.id === item.id,
        })}
        onClick={() => setSelectedPlan(item)}
        key={item.id}
      >
        <FormControlLabel
          disabled={disabled}
          value={item.id}
          control={
            <Radio
              className={classes.root}
              disabled={disabled}
              disableRipple
              color="default"
              checkedIcon={
                <span className={classnames(classes.icon, classes.checkedIcon)} />
              }
              icon={<span className={classes.icon} />}
            />
          }
          label={item.name}
        />
        <div className={styles.priceTag}>
          <b>{formatPrice(item.price, item.currency)}</b>
          <span className={styles.perUnit}>
            {`${formatPrice(calculatePrice(item), item.currency)}${t("Common.comparePlans.perSession")}`}
          </span>
        </div>
      </div>
    ));

  return (
    <RadioGroup
      defaultValue={selectedPlan?.id}
      value={selectedPlan?.id}
      onChange={hdChangePlan}
    >
      {selfStudyRadioGroup}
      {oneOnOneRadioGroup}
    </RadioGroup>
  );
}
